import type { CompanyAddressType, CountryCode } from '@shopify/hydrogen/customer-account-api-types';

import type { WholesaleApiErrorResponse } from './misc';

export type DropFirst<T extends unknown[]> = T extends [unknown, ...infer U] ? U : never;

// Custom type guard for country code
export const isCountryCode = (value: FormDataEntryValue | string): value is CountryCode => !!value;

export const isCompanyAddressType = (value: FormDataEntryValue | string): value is CompanyAddressType =>
  value === 'BILLING' || value === 'SHIPPING';

export const isKeyof = <T extends object>(obj: T, key?: string | number | symbol): key is keyof T =>
  !!key && key in obj;

// Type guard for Wholesale API Error Response
export const isWholesaleApiErrorResponse = (response: unknown): response is WholesaleApiErrorResponse => {
  return Boolean(
    response &&
      typeof response === 'object' &&
      'body' in response &&
      response.body &&
      typeof response.body === 'object' &&
      'error' in response.body &&
      response.body.error &&
      typeof response.body.error === 'object' &&
      'message' in response.body.error &&
      typeof response.body.error.message === 'string',
  );
};

/**
 * Generic utility to extract components from any Sanity query result.
 * T should be the query result type, K should be the key where components are stored.
 */
export type ExtractComponents<T, K extends keyof NonNullable<T>> = NonNullable<NonNullable<T>[K]>;

/**
 * Generic utility to extract a specific component type from a components array.
 * T should be the components array type, U should be the _type value to extract.
 */
export type ExtractComponentByType<U extends string, T extends Record<string, unknown>[] = []> = Extract<
  T[number],
  { _type: U }
>;

// Get the component without the _type or key properties. We use this to pass props to the component.
export type OmitTypeAndKey<T> = Omit<T, '_type' | '_key'>;
